import { useGetSettingsPermissionsAudit } from '@agos/api-client/auth'
import { formatCount, T, tt } from '@agos/shared'
import { cn, FilterSelect, Spinner } from '@agos/ui'
import { useMemo, useState } from 'react'

import { useRolesOverview } from '../../roles/roles-api'
import {
  actionText,
  auditChange,
  byMostRecent,
  displayParentOf,
  parseStamp,
  permissionLabel,
  type AuditEntry,
} from '../permission-matrix'
import { orderRoles, roleNameOf, verbsText } from '../role-model'
import { TabReadFailed } from '../TabReadFailed'
import {
  actorRoleText,
  edgeChangesOf,
  filterAudit,
  isFromDelegate,
  roleDeletionOf,
  roleFieldChangesOf,
  roleFieldLabel,
  roleMoveOf,
  targetText,
  type AuditFilter,
} from './audit-detail'

/**
 * *História* - who changed whose rights, and since O119 who moved whom between roles and who may hand which role down.
 * The main administrator's tab (`FRONTEND.md` §9.752).
 *
 * **Moved from the old screen with its sentence kept**: *Nikto zatiaľ práva nemenil* is a claim about the trail, so it
 * is drawn only over a read that answered (FB-822, §9.543). What is new: the actor's role (`actorRole`, the Control
 * Tower as itself), a role change as *z Čašník na Barman*, delegation edges added and removed, rights by their names,
 * and filters by role, person, actor and *len zmeny od vedúcich* - client-side over the 200 entries read.
 *
 * Role names come from the overview when it answered and from the built-in labels when it did not: a failed overview
 * makes a name plainer, never the trail absent.
 */
export function HistoryTab() {
  const audit = useGetSettingsPermissionsAudit({ limit: 200 })
  const overview = useRolesOverview()
  const [filter, setFilter] = useState<AuditFilter>({ role: '', person: '', actor: '', delegatesOnly: false })
  const entries = useMemo(
    () => byMostRecent(((audit.data ?? {}) as { entries?: AuditEntry[] }).entries ?? []),
    [audit.data],
  )
  const roles = useMemo(() => orderRoles(overview.data?.roles ?? []), [overview.data?.roles])
  const known = useMemo(() => new Set(overview.data?.allPermissions ?? []), [overview.data?.allPermissions])

  if (audit.isError) {
    return <TabReadFailed what="audit" retry={() => void audit.refetch()} />
  }

  if (audit.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <p className="text-xs text-muted-foreground py-8 text-center">
        <T>Nikto zatiaľ práva nemenil.</T>
      </p>
    )
  }

  const nameOf = (key: string) => roleNameOf(roles, key)
  const labelOf = (token: string) => {
    // `auditChange` marks a denial with a trailing cross; the right is named and the mark kept.
    const denied = token.endsWith(' ✕')
    const key = denied ? token.slice(0, -2) : token
    const label = permissionLabel(key, displayParentOf(key, known))
    return `${label === key ? key : `${label} (${key})`}${denied ? ' ✕' : ''}`
  }
  const shown = filterAudit(entries, filter)
  const people = uniqueBy(
    entries
      .filter((entry) => entry.targetType === 'employee' && entry.targetKey !== undefined)
      .map((entry) => ({ value: entry.targetKey ?? '', label: entry.targetName ?? entry.targetKey ?? '' })),
  )
  const actors = uniqueBy(
    entries
      .filter((entry) => (entry.actorName ?? '') !== '')
      .map((entry) => ({ value: entry.actorName ?? '', label: entry.actorName ?? '' })),
  )

  return (
    <div className="space-y-3" data-history-tab>
      <div className="flex flex-wrap items-center gap-2" data-audit-filters>
        <FilterSelect
          value={filter.role}
          onChange={(role) => setFilter({ ...filter, role })}
          options={[
            { value: '', label: tt('Všetky role') },
            ...roles.map((role) => ({ value: role.key, label: nameOf(role.key) })),
          ]}
          label={tt('Rola')}
          testId="data-audit-filter-role"
        />
        <FilterSelect
          value={filter.person}
          onChange={(person) => setFilter({ ...filter, person })}
          options={[{ value: '', label: tt('Všetci ľudia') }, ...people]}
          label={tt('Človek')}
          testId="data-audit-filter-person"
        />
        <FilterSelect
          value={filter.actor}
          onChange={(actor) => setFilter({ ...filter, actor })}
          options={[{ value: '', label: tt('Ktokoľvek zmenil') }, ...actors]}
          label={tt('Kto zmenil')}
          testId="data-audit-filter-actor"
        />
        <button
          type="button"
          onClick={() => setFilter({ ...filter, delegatesOnly: !filter.delegatesOnly })}
          aria-pressed={filter.delegatesOnly}
          data-audit-filter-delegates={filter.delegatesOnly}
          className={cn(
            'px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-colors',
            filter.delegatesOnly
              ? 'bg-primary/10 border-primary text-primary'
              : 'border-border text-muted-foreground hover:text-foreground',
          )}
        >
          {tt('Len zmeny od vedúcich')}
        </button>
      </div>

      {shown.length === 0 && (
        <p className="text-xs text-muted-foreground py-6 text-center" data-audit-none-matching>
          {tt('Filtru nezodpovedá žiadna zmena.')}
        </p>
      )}

      <div className="space-y-1.5" data-audit>
        {shown.map((entry) => {
          const change = auditChange(entry)
          const move = roleMoveOf(entry, nameOf)
          const edges = edgeChangesOf(entry)
          const fields = roleFieldChangesOf(entry)
          const deletion = roleDeletionOf(entry)
          const target = targetText(entry, nameOf)
          const actorRole = actorRoleText(entry, nameOf, tt('Control Tower'))
          const said = move !== null || edges !== null || (fields !== null && fields.length > 0) || deletion !== null
          return (
            <div
              key={entry.id}
              className="rounded-xl border border-border bg-card px-3 py-2.5 space-y-1"
              data-audit-entry={entry.id}
              data-audit-from-delegate={isFromDelegate(entry)}
            >
              <p className="text-xs">
                <strong>{entry.actorName}</strong>
                {actorRole !== null && (
                  <span className="text-muted-foreground" data-audit-actor-role>
                    {' '}
                    ({actorRole})
                  </span>
                )}{' '}
                {actionText(entry.action)}
                {/* The delegation graph has no name worth printing - the action already says what changed. */}
                {target !== '' && (
                  <>
                    {' '}
                    <strong>{target}</strong>
                  </>
                )}
                <span className="text-muted-foreground"> · {formatStamp(entry.createdAt)}</span>
              </p>
              {move !== null && (
                <p className="text-[11px]" data-audit-role-move>
                  {tt('z')} <strong>{move.from}</strong> {tt('na')} <strong>{move.to}</strong>
                  {move.signedOut && <span className="text-amber-500"> · {tt('odhlásený na všetkých zariadeniach')}</span>}
                </p>
              )}
              {fields !== null &&
                fields.map((change) => (
                  <p key={change.field} className="text-[11px]" data-audit-role-field={change.field}>
                    {roleFieldLabel(change.field)}: {tt('z')} <strong>{change.from ?? '-'}</strong> {tt('na')}{' '}
                    <strong>{change.to ?? '-'}</strong>
                  </p>
                ))}
              {deletion !== null && deletion.movedTo !== null && (
                <p className="text-[11px]" data-audit-role-deleted>
                  {tt('Jej ľudia')} ({formatCount(deletion.moved)}) {tt('presunutí do')}{' '}
                  <strong>{nameOf(deletion.movedTo)}</strong>
                  {deletion.signedOut > 0 && (
                    <span className="text-amber-500">
                      {' '}
                      · {tt('odhlásení')}: {formatCount(deletion.signedOut)}
                    </span>
                  )}
                </p>
              )}
              {edges !== null &&
                edges.added.map((edge) => (
                  <p key={`+${edge.manager}>${edge.target}`} className="text-[11px] text-emerald-500" data-audit-edge-added>
                    + {nameOf(edge.manager)} {tt('spravuje')} {nameOf(edge.target)} ({verbsText(edge)})
                  </p>
                ))}
              {edges !== null &&
                edges.removed.map((edge) => (
                  <p key={`-${edge.manager}>${edge.target}`} className="text-[11px] text-red-500" data-audit-edge-removed>
                    − {nameOf(edge.manager)} {tt('spravuje')} {nameOf(edge.target)} ({verbsText(edge)})
                  </p>
                ))}
              {/* Legacy prints the two snapshots as JSON; the reader wants the right that moved, by its name. */}
              {change.added.length > 0 && (
                <p className="text-[11px] text-emerald-500" data-audit-added>
                  + {change.added.map(labelOf).join(', ')}
                </p>
              )}
              {change.removed.length > 0 && (
                <p className="text-[11px] text-red-500" data-audit-removed>
                  − {change.removed.map(labelOf).join(', ')}
                </p>
              )}
              {!said && change.added.length === 0 && change.removed.length === 0 && (
                <p className="text-[11px] text-muted-foreground">
                  <T>Bez zmeny v právach.</T>
                </p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function uniqueBy(options: { value: string; label: string }[]): { value: string; label: string }[] {
  const seen = new Map<string, { value: string; label: string }>()
  for (const option of options) {
    if (option.value !== '' && !seen.has(option.value)) seen.set(option.value, option)
  }
  return [...seen.values()].sort((left, right) => left.label.localeCompare(right.label))
}

function formatStamp(value: string | null | undefined): string {
  const stamp = parseStamp(value)
  if (stamp === null) return '-'
  return new Date(stamp).toLocaleString('sk-SK')
}
